import React, { useEffect, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';
import { AdminMetricsPanel } from './AdminMetricsPanel';
import { TileEditorPanel } from './TileEditorPanel';
import { AdminPanelWorldTab } from './AdminPanelWorldTab';
import { AdminPanelPlayersTab } from './AdminPanelPlayersTab';
import { AdminPanelSanctumTab } from './AdminPanelSanctumTab';
import { AdminPanelToolsTab } from './AdminPanelToolsTab';
import { AdminPanelCombatTestingTab } from './AdminPanelCombatTestingTab';

// ============ ADMIN PANEL COMPONENT ============

interface AdminPanelProps {
  apiBaseUrl: string;
  socket?: Socket | null;
  playerId?: string;
  onToggleCollisionDebug?: (enabled: boolean) => void;
  onToggleHitboxDebug?: (enabled: boolean) => void;
  currentTile?: { x: number; y: number };
  onBerriesChange?: (amount: number) => void;
  setLoadingOverlayVisible?: (visible: boolean) => void;
  setLoadingOverlayMessage?: (message: string) => void;
  setIsInstanceEntry?: (isInstance: boolean) => void;
}

interface AdminActionState {
  isBusy: boolean;
  lastMessage: string | null;
}

type AdminTab = 'world' | 'players' | 'sanctum' | 'tools' | 'combat';

export const AdminPanel: React.FC<AdminPanelProps> = ({ apiBaseUrl, socket, playerId, onToggleCollisionDebug, onToggleHitboxDebug, currentTile, onBerriesChange, setLoadingOverlayVisible, setLoadingOverlayMessage, setIsInstanceEntry }) => {
  const [state, setState] = useState<AdminActionState>({
    isBusy: false,
    lastMessage: null,
  });

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<AdminTab>('world');
  const [showMetrics, setShowMetrics] = useState<boolean>(false);
  const [showTileEditor, setShowTileEditor] = useState<boolean>(false);
  const userInitiatedRef = useRef<boolean>(false);

  // Only show panel if user explicitly toggled it open
  const shouldShowPanel: boolean = userInitiatedRef.current && isOpen;

  // Reset panel on mount to prevent hot-reload state preservation
  useEffect(() => {
    userInitiatedRef.current = false;
    setIsOpen(false);
  }, []);

  const togglePanel = (): void => {
    userInitiatedRef.current = true;
    setIsOpen((prev) => !prev);
  };

  if (!apiBaseUrl) {
    return null;
  }

  return (
    <div
      className="admin-panel-dev font-mono"
      style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        padding: 8,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        border: '1px solid #4a5568',
        borderRadius: 0,
        color: '#e2e8f0',
        fontSize: 10,
        zIndex: 9999,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: shouldShowPanel ? 4 : 0,
        }}
      >
        <div style={{ fontWeight: 600, marginRight: 5 }}>DEV ADMIN</div>
        <button
          type="button"
          onClick={togglePanel}
          style={{
            padding: '0 4px',
            fontSize: 10,
            backgroundColor: '#1f2933',
            color: '#e2e8f0',
            border: '1px solid #4a5568',
            borderRadius: 0,
            cursor: 'pointer',
          }}
        >
          {shouldShowPanel ? '−' : '+'}
        </button>
      </div>

      {shouldShowPanel && (
        <>
          {/* Tab Navigation */}
          <div style={{ display: 'flex', gap: 2, marginBottom: 4, borderBottom: '1px solid #4a5568', paddingBottom: 4 }}>
            <button
              onClick={() => setActiveTab('world')}
              style={{
                padding: '2px 8px',
                backgroundColor: activeTab === 'world' ? '#2d3748' : '#1f2933',
                border: activeTab === 'world' ? '1px solid #4a5568' : '1px solid #2d3748',
                color: activeTab === 'world' ? '#e2e8f0' : '#a0aec0',
                cursor: 'pointer',
                fontSize: 9,
                fontWeight: activeTab === 'world' ? 600 : 400,
              }}
            >
              🌍 World
            </button>
            <button
              onClick={() => setActiveTab('players')}
              style={{
                padding: '2px 8px',
                backgroundColor: activeTab === 'players' ? '#2d3748' : '#1f2933',
                border: activeTab === 'players' ? '1px solid #4a5568' : '1px solid #2d3748',
                color: activeTab === 'players' ? '#e2e8f0' : '#a0aec0',
                cursor: 'pointer',
                fontSize: 9,
                fontWeight: activeTab === 'players' ? 600 : 400,
              }}
            >
              👥 Players
            </button>
            <button
              onClick={() => setActiveTab('sanctum')}
              style={{
                padding: '2px 8px',
                backgroundColor: activeTab === 'sanctum' ? '#2d3748' : '#1f2933',
                border: activeTab === 'sanctum' ? '1px solid #4a5568' : '1px solid #2d3748',
                color: activeTab === 'sanctum' ? '#e2e8f0' : '#a0aec0',
                cursor: 'pointer',
                fontSize: 9,
                fontWeight: activeTab === 'sanctum' ? 600 : 400,
              }}
            >
              🏛️ Sanctum
            </button>
            <button
              onClick={() => setActiveTab('tools')}
              style={{
                padding: '2px 8px',
                backgroundColor: activeTab === 'tools' ? '#2d3748' : '#1f2933',
                border: activeTab === 'tools' ? '1px solid #4a5568' : '1px solid #2d3748',
                color: activeTab === 'tools' ? '#e2e8f0' : '#a0aec0',
                cursor: 'pointer',
                fontSize: 9,
                fontWeight: activeTab === 'tools' ? 600 : 400,
              }}
            >
              🔧 Tools
            </button>
            <button
              onClick={() => setActiveTab('combat')}
              style={{
                padding: '2px 8px',
                backgroundColor: activeTab === 'combat' ? '#2d3748' : '#1f2933',
                border: activeTab === 'combat' ? '1px solid #4a5568' : '1px solid #2d3748',
                color: activeTab === 'combat' ? '#ffd700' : '#a0aec0',
                cursor: 'pointer',
                fontSize: 9,
                fontWeight: activeTab === 'combat' ? 600 : 400,
              }}
            >
              ⚔️ Combat
            </button>
          </div>

          {/* Content Area - Scrollable */}
          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 'calc(80vh - 200px)' }}>
            {/* World Tab */}
            {activeTab === 'world' && (
              <AdminPanelWorldTab
                apiBaseUrl={apiBaseUrl}
                socket={socket}
                playerId={playerId}
                isBusy={state.isBusy}
                onStateChange={setState}
              />
            )}

            {/* Players Tab */}
            {activeTab === 'players' && (
              <AdminPanelPlayersTab
                apiBaseUrl={apiBaseUrl}
                socket={socket}
                playerId={playerId}
                isBusy={state.isBusy}
                onStateChange={setState}
              />
            )}

            {/* Sanctum Tab */}
            {activeTab === 'sanctum' && (
              <AdminPanelSanctumTab
                socket={socket}
                playerId={playerId}
                isBusy={state.isBusy}
                onStateChange={setState}
                setLoadingOverlayVisible={setLoadingOverlayVisible}
                setLoadingOverlayMessage={setLoadingOverlayMessage}
                setIsInstanceEntry={setIsInstanceEntry}
              />
            )}

            {/* Tools Tab */}
            {activeTab === 'tools' && (
              <AdminPanelToolsTab
                apiBaseUrl={apiBaseUrl}
                socket={socket}
                playerId={playerId}
                currentTile={currentTile}
                isBusy={state.isBusy}
                onStateChange={setState}
                onToggleCollisionDebug={onToggleCollisionDebug}
                onToggleHitboxDebug={onToggleHitboxDebug}
              />
            )}

            {/* Combat Testing Tab */}
            {activeTab === 'combat' && (
              <AdminPanelCombatTestingTab
                socket={socket}
                playerId={playerId}
                isBusy={state.isBusy}
                onStateChange={setState}
              />
            )}
          </div>

          {/* Status Display */}
          <div style={{ marginTop: 6, fontSize: 9, color: '#718096' }}>
            ID: {playerId ? playerId.substring(0, 12) + '...' : 'not connected'} {state.isBusy ? '(busy)' : ''}
          </div>

          {state.lastMessage && (
            <div style={{ marginTop: 4, maxWidth: 220 }}>{state.lastMessage}</div>
          )}
        </>
      )}

      {/* Metrics Panel Modal */}
      <AdminMetricsPanel isVisible={showMetrics} onClose={() => setShowMetrics(false)} />

      {/* Tile Editor Modal */}
      {showTileEditor && (
        <TileEditorPanel
          onClose={() => setShowTileEditor(false)}
          accountInfo={{ username: playerId || 'admin' }}
        />
      )}
    </div>
  );
};

export default AdminPanel;
